import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Navigation,
  ArrowRight,
  Car,
  Star,
  Cloud,
  Wrench,
  Tag,
  Search,
  FlaskConical,
  CheckCircle2,
  Send,
  Timer,
  Users,
  BadgeCheck,
  MapPin,
  Mail,
  Clock,
  Check,
  Plus,
  Minus,
} from "lucide-react";
import { BUSINESS, IMAGES } from "../data/site";
import { SEO } from "../components/SEO";
import { Coupon } from "../components/Coupon";
import { FadeUp, MaskedLine, Overline } from "../components/Reveal";

const Hero = () => (
  <section data-testid="hero" className="relative overflow-hidden bg-white">
    <div className="absolute inset-0 bg-gradient-to-br from-[#F6F9FD] via-[#E2EDFC] to-[#DEF4EA]" aria-hidden="true" />
    <div className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-sky-300/45 blur-3xl" aria-hidden="true" />
    <div className="absolute -bottom-40 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-200/55 blur-3xl" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="hero-badge"
          className="inline-flex items-center gap-2 bg-white/70 border border-line backdrop-blur text-ink font-bold text-xs uppercase tracking-[0.14em] px-4 py-2 rounded-full"
        >
          <Star className="w-4 h-4 text-red-600 fill-red-600" />
          STAR Certified <span className="text-volt">Smog Check Station</span>
        </motion.p>

        <h1 className="mt-6 font-display font-black tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-[3.6rem] text-ink">
          <MaskedLine delay={0.15}>Fast & Reliable</MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="text-volt">Smog Check in Hayward, CA</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-xl"
        >
          Visit BaySmog at 258 W Tennyson Rd in Hayward for fast, professional smog testing and
          convenient vehicle services — minutes from I-880 & Tennyson Road.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-3.5"
        >
          <a
            data-testid="hero-call-button"
            href={BUSINESS.phoneHref}
            className="inline-flex items-center gap-2.5 bg-gradient-to-r from-volt to-[#0B4AC2] text-white rounded-full font-display font-bold uppercase tracking-wide px-9 py-4 shadow-lg shadow-volt/40 hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] transition-[box-shadow,transform] duration-300"
          >
            <Phone className="w-5 h-5" />
            Call Now
          </a>
          <a
            data-testid="hero-directions-button"
            href={BUSINESS.mapsDirections}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 border border-volt/40 text-volt bg-white/80 backdrop-blur rounded-full font-display font-bold uppercase tracking-wide px-9 py-4 hover:bg-volt hover:text-white hover:-translate-y-1 transition-[background-color,color,transform] duration-300"
          >
            <Navigation className="w-5 h-5" />
            Get Directions
          </a>
          <Link
            data-testid="hero-smogcheck-button"
            to="/contact"
            className="inline-flex items-center gap-2.5 bg-gradient-to-r from-cta to-[#F59E0B] text-ink rounded-full font-display font-bold uppercase tracking-wide px-9 py-4 shadow-lg shadow-cta/40 hover:brightness-95 hover:-translate-y-1 hover:scale-[1.02] transition-[transform,filter] duration-300"
          >
            <CheckCircle2 className="w-5 h-5" />
            Get a Smog Check
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-8 flex items-center gap-2 text-sm text-slate-500"
        >
          <Clock className="w-4 h-4 text-volt" />
          Open 6 days a week · {BUSINESS.hoursShort}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <div className="rounded-2xl overflow-hidden border border-line shadow-xl">
          <picture>
            <source
              type="image/webp"
              srcSet="/storefront-640.webp 640w, /storefront-960.webp 960w, /storefront-1200.webp 1200w"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
            <img
              src={IMAGES.shop}
              alt="Vehicle receiving a professional smog check at BaySmog in Hayward, CA"
              className="w-full h-[340px] sm:h-[420px] lg:h-[480px] object-cover"
              width="1200"
              height="800"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="absolute top-5 right-4 sm:-right-4 bg-white text-ink rounded-xl shadow-lg px-5 py-3.5"
        >
          <p className="font-display font-black text-lg leading-none text-volt">15–20 Min</p>
          <p className="text-[11px] text-slate-500 mt-1">Average Smog Test</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          className="absolute -bottom-5 left-4 sm:-left-5 bg-white rounded-xl shadow-lg border border-line px-5 py-4 flex items-center gap-3"
        >
          <span className="w-10 h-10 rounded-lg bg-volt-dim flex items-center justify-center shrink-0">
            <Star className="w-5 h-5 text-volt fill-volt" />
          </span>
          <span>
            <span className="block font-display font-bold text-ink text-sm leading-tight">STAR Certified</span>
            <span className="block text-xs text-slate-500 mt-0.5">All DMV-Directed Vehicles</span>
          </span>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

const STATS = [
  { icon: Timer, big: "15–20 Min", small: "Average Smog Test" },
  { icon: Users, big: "Walk-Ins", small: "Always Welcome" },
  { icon: Star, big: "STAR", small: "Certified Station" },
  { icon: Send, big: "Instant", small: "Electronic Results" },
];

const StatsStrip = () => (
  <section data-testid="stats-strip" className="bg-white border-y border-line">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <FadeUp
          key={s.small}
          delay={i * 0.06}
          className={`flex items-center gap-4 py-7 ${i > 0 ? "lg:border-l lg:border-line lg:pl-8" : ""} ${i % 2 === 1 ? "border-l border-line pl-6 lg:pl-8" : ""}`}
        >
          <span className="w-11 h-11 rounded-lg bg-volt-dim flex items-center justify-center shrink-0">
            <s.icon className="w-5 h-5 text-volt" />
          </span>
          <span>
            <span className="block font-display font-extrabold text-lg text-ink leading-tight">{s.big}</span>
            <span className="block text-xs text-slate-500 mt-0.5">{s.small}</span>
          </span>
        </FadeUp>
      ))}
    </div>
  </section>
);

const SERVICE_CARDS = [
  {
    icon: Car,
    title: "Smog Check",
    text: "Regular smog check for all vehicles. Fast, reliable and DMV approved.",
    link: "/services/smog-check-hayward",
    cta: "Learn More",
  },
  {
    icon: Star,
    title: "STAR Smog Check",
    text: "STAR certified smog check for vehicles that require a STAR station.",
    link: "/services/star-smog-check-hayward",
    cta: "Learn More",
  },
  {
    icon: Cloud,
    title: "Emissions Testing",
    text: "Comprehensive emissions testing to keep your vehicle compliant.",
    link: "/services/diesel-smog-check-hayward",
    cta: "Learn More",
  },
  {
    icon: Wrench,
    title: "Smog Repairs",
    text: "Failed your test? Honest diagnostics and smog-related repairs.",
    link: "/services/smog-repair-hayward",
    cta: "Learn More",
  },
  {
    icon: Tag,
    title: "Coupons & Specials",
    text: "Save with our latest smog check coupons and special offers.",
    link: "#coupon",
    cta: "View Coupons",
  },
];

const ServicesCards = () => (
  <section data-testid="services-section" className="py-20 md:py-24 bg-paper">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <FadeUp className="text-center max-w-2xl mx-auto">
        <Overline className="text-center">Our Services</Overline>
        <h2 className="mt-4 font-display font-black tracking-tight text-3xl md:text-5xl text-ink">
          Complete Smog Check Solutions
        </h2>
        <p className="mt-4 text-slate-600">
          Everything your vehicle needs to pass the California smog check — handled in one quick visit.
        </p>
      </FadeUp>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {SERVICE_CARDS.map((s, i) => {
          const inner = (
            <>
              <span className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center">
                <s.icon className="w-7 h-7 text-volt" strokeWidth={1.7} />
              </span>
              <h3 className="mt-5 font-display font-bold text-lg text-ink group-hover:text-volt transition-colors duration-300">
                {s.title}
              </h3>
              <p className="mt-2.5 text-sm text-slate-500 leading-relaxed flex-1">{s.text}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-volt">
                {s.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </>
          );
          const cls =
            "group flex flex-col items-center text-center h-full bg-[#EAF2FE] border border-[#D3E3FA] rounded-xl shadow-sm p-7 hover:border-volt hover:shadow-md hover:-translate-y-1 transition-[border-color,box-shadow,transform] duration-300";
          const testid = `service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
          return (
            <FadeUp key={s.title} delay={i * 0.07}>
              {s.link.startsWith("#") ? (
                <a
                  data-testid={testid}
                  href={s.link}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("coupon")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={cls}
                >
                  {inner}
                </a>
              ) : (
                <Link data-testid={testid} to={s.link} className={cls}>
                  {inner}
                </Link>
              )}
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

const WHY = [
  { icon: Timer, title: "Fast Service", text: "In & Out Quickly" },
  { icon: Tag, title: "Affordable Prices", text: "Call for Current Pricing" },
  { icon: Users, title: "Walk-Ins Welcome", text: "No Appointment Needed" },
  { icon: BadgeCheck, title: "Experienced Technicians", text: "Professional & Certified" },
];

const WhyChoose = () => (
  <section data-testid="why-section" className="py-20 md:py-24 bg-white border-b border-line">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-center">
      <FadeUp className="lg:col-span-4">
        <Overline>Why Choose BaySmog</Overline>
        <h2 className="mt-4 font-display font-black tracking-tight text-3xl md:text-4xl text-ink">
          Trusted by <span className="text-volt">Hayward</span> Drivers
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed">
          We are committed to providing the best smog check experience with honest service, fair
          pricing and professional care.
        </p>
        <a
          data-testid="why-call-button"
          href={BUSINESS.phoneHref}
          className="mt-7 inline-flex items-center gap-2.5 bg-volt text-white rounded-lg font-display font-bold uppercase tracking-wide text-sm px-7 py-3.5 hover:bg-volt-hover transition-colors duration-300"
        >
          <Phone className="w-4 h-4" /> {BUSINESS.phone}
        </a>
      </FadeUp>
      <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {WHY.map((w, i) => (
          <FadeUp
            key={w.title}
            delay={i * 0.08}
            className="text-center bg-paper border border-line rounded-xl p-6 hover:border-volt transition-colors duration-300"
          >
            <div className="mx-auto w-14 h-14 rounded-xl bg-white border border-line flex items-center justify-center">
              <w.icon className="w-7 h-7 text-volt" strokeWidth={1.7} />
            </div>
            <h3 className="mt-4 font-display font-bold text-ink">{w.title}</h3>
            <p className="mt-1 text-sm text-slate-500">{w.text}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  { icon: Car, title: "Visit Us", text: "Come to our shop at 258 W Tennyson Rd, Hayward, CA 94544" },
  { icon: Search, title: "Inspection", text: "We perform a visual inspection of your vehicle." },
  { icon: FlaskConical, title: "Smog Test", text: "We run the official smog test on state equipment." },
  { icon: CheckCircle2, title: "Results", text: "You receive your results immediately." },
  { icon: Send, title: "Results Sent", text: "We electronically submit your results — done." },
];

const Process = () => (
  <section data-testid="process-section" className="py-20 md:py-24 bg-paper">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <FadeUp className="max-w-2xl">
        <Overline>How It Works</Overline>
        <h2 className="mt-4 font-display font-black tracking-tight text-3xl md:text-4xl text-ink">
          Our Smog Check Process
        </h2>
      </FadeUp>
      <div className="mt-14 relative">
        <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] border-t-2 border-dashed border-volt/30" aria-hidden="true" />
        <ol className="grid sm:grid-cols-3 lg:grid-cols-5 gap-10 lg:gap-6">
          {STEPS.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.08}>
              <li className="relative text-center" data-testid={`process-step-${i + 1}`}>
                <div className="relative z-10 mx-auto w-16 h-16 rounded-full bg-volt shadow-md shadow-volt/25 flex items-center justify-center">
                  <s.icon className="w-7 h-7 text-white" strokeWidth={1.9} />
                </div>
                <h3 className="mt-5 font-display font-bold text-ink">
                  <span className="text-volt mr-1.5">{i + 1}.</span>
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.text}</p>
              </li>
            </FadeUp>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const GOOGLE_G = (
  <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2.1 3.5-5.1 3.5-8.6z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5l-.14.01-3.1 2.4-.04.14C3.9 21.3 7.7 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4l-.01-.16-3.1-2.4-.1.05C1.3 8.6 1 10.2 1 12s.3 3.4 1 4.9l3.2-2.5z" />
    <path fill="#EA4335" d="M12 4.6c2.3 0 3.8 1 4.7 1.8l3.4-3.3C17.9 1.1 15.2 0 12 0 7.7 0 3.9 2.7 2.1 6.6l3.1 2.4c1-2.9 3.7-4.4 6.8-4.4z" />
  </svg>
);

const ReviewsCTA = () => (
  <section data-testid="reviews-section" className="py-20 md:py-24 bg-white border-b border-line">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 items-center">
      <FadeUp className="lg:col-span-5">
        <Overline>Google Reviews</Overline>
        <h2 className="mt-4 font-display font-black tracking-tight text-3xl md:text-4xl text-ink">
          What Our Customers Say
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed max-w-lg">
          Hayward drivers rate BaySmog among the best smog check stations in the East Bay. Read their
          real reviews — and share your own experience — on Google.
        </p>
        <div className="mt-8 flex flex-wrap gap-3.5">
          <a
            data-testid="reviews-google-button"
            href={BUSINESS.mapsPlace}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-gradient-to-r from-volt to-[#0B4AC2] text-white rounded-full font-display font-bold uppercase tracking-wide text-sm px-7 py-3.5 shadow-lg shadow-volt/40 hover:-translate-y-0.5 transition-transform duration-300"
          >
            Read All Reviews <ArrowRight className="w-4 h-4" />
          </a>
          <a
            data-testid="reviews-write-button"
            href={BUSINESS.mapsPlace}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 border border-volt/40 text-volt bg-white/80 rounded-full font-display font-bold uppercase tracking-wide text-sm px-7 py-3.5 hover:bg-volt hover:text-white transition-[background-color,color] duration-300"
          >
            Write a Review
          </a>
        </div>
      </FadeUp>
      <FadeUp delay={0.12} className="lg:col-span-7">
        <div className="bg-paper border border-line rounded-2xl shadow-sm p-8 md:p-10">
          <div className="flex flex-wrap items-center gap-8 md:gap-12">
            <div className="text-center" data-testid="reviews-rating-block">
              <p className="font-display font-black text-7xl leading-none text-ink">4.8</p>
              <div className="flex gap-1 mt-3 justify-center" aria-label="4.8 out of 5 stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-cta fill-cta" />
                ))}
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-500">Based on 109 reviews</p>
            </div>
            <div className="flex-1 min-w-[230px]">
              <div className="flex items-center gap-3">
                {GOOGLE_G}
                <p className="font-display font-bold text-lg text-ink">Google Rating</p>
              </div>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                Verified rating from BaySmog's live Google Business Profile — updated in real time on
                Google Maps.
              </p>
              <a
                data-testid="reviews-profile-link"
                href={BUSINESS.mapsPlace}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-volt hover:underline"
              >
                <BadgeCheck className="w-4 h-4" />
                BAY SMOG CHECK · Hayward, CA
              </a>
            </div>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

const FAQS = [
  {
    q: "Do I need an appointment for a smog check?",
    a: "No appointment is necessary. BaySmog welcomes walk-ins Monday through Saturday — just drive up to 258 W Tennyson Rd in Hayward and we'll get you tested.",
  },
  {
    q: "How long does a smog check take?",
    a: "Most smog checks are completed in about 15–20 minutes. Results are sent electronically to the DMV the same visit.",
  },
  {
    q: "My DMV notice says I need a STAR smog check. Can you do it?",
    a: "Yes. BaySmog is a STAR-certified station, authorized to test all vehicles — including those the DMV specifically directs to a STAR station.",
  },
  {
    q: "Do you smog check diesel vehicles?",
    a: "Yes — we perform diesel smog checks for cars, trucks, vans and RVs, plus Clean Truck Check testing for heavy-duty trucks over 14,000 lbs.",
  },
  {
    q: "What should I bring for my smog check?",
    a: "Bring your DMV renewal notice (if you have one) and your current registration. Payment is due at the time of the test.",
  },
  {
    q: "Does the DMV require a smog check for my renewal?",
    a: "Most California registration renewals require a smog check. Bring your DMV renewal notice — we'll test your vehicle and transmit the certificate electronically to the DMV the same visit.",
  },
  {
    q: "What happens if my vehicle fails the smog check?",
    a: "We'll explain exactly why it failed in plain language and outline your repair options. After repairs, bring it back for a retest — and ask us about California's Consumer Assistance Program if cost is a concern.",
  },
];

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section data-testid="faq-section" className="py-20 md:py-24 bg-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-4">
          <Overline>FAQ</Overline>
          <h2 className="mt-4 font-display font-black tracking-tight text-3xl md:text-4xl text-ink">
            Frequently Asked Questions
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Everything Hayward drivers ask us about smog checks and STAR inspections.
          </p>
          <a
            data-testid="faq-call-button"
            href={BUSINESS.phoneHref}
            className="mt-7 inline-flex items-center gap-2.5 bg-volt text-white rounded-lg font-display font-bold uppercase tracking-wide text-sm px-7 py-3.5 hover:bg-volt-hover transition-colors duration-300"
          >
            <Phone className="w-4 h-4" /> Ask Us Directly
          </a>
        </FadeUp>
        <FadeUp delay={0.12} className="lg:col-span-8">
          <div className="space-y-3" data-testid="faq-list">
            {FAQS.map((f, i) => {
              const open = openIdx === i;
              return (
                <div
                  key={f.q}
                  className={`bg-white border rounded-xl overflow-hidden transition-colors duration-300 ${open ? "border-volt" : "border-line"}`}
                >
                  <button
                    data-testid={`faq-question-${i + 1}`}
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    aria-expanded={open}
                    className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                  >
                    <span className="font-display font-bold text-ink">{f.q}</span>
                    <span
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${open ? "bg-volt text-white" : "bg-volt-dim text-volt"}`}
                    >
                      {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-slate-600 leading-relaxed text-sm md:text-base">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const VISIT_CHECKLIST = [
  "STAR Certified Smog Check Station",
  "Licensed & Insured Technicians",
  "Latest Testing Equipment",
  "Honest & Transparent Service",
  "Serving Hayward & Surrounding Areas",
];

const VisitUs = () => (
  <section data-testid="visit-section" className="py-20 md:py-24 bg-paper">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="grid lg:grid-cols-12 gap-10">
        <FadeUp className="lg:col-span-4">
          <h2 className="font-display font-black tracking-tight text-3xl md:text-4xl text-ink">Visit BaySmog</h2>
          <address className="not-italic mt-7 space-y-5">
            <a
              data-testid="visit-address-link"
              href={BUSINESS.mapsPlace}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 text-slate-700 hover:text-volt transition-colors duration-300"
            >
              <MapPin className="w-5 h-5 text-volt shrink-0 mt-0.5" />
              <span className="font-semibold leading-snug">
                {BUSINESS.street}
                <br />
                {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}
              </span>
            </a>
            <a
              data-testid="visit-phone-link"
              href={BUSINESS.phoneHref}
              className="flex items-center gap-3 text-slate-700 hover:text-volt transition-colors duration-300"
            >
              <Phone className="w-5 h-5 text-volt shrink-0" />
              <span className="font-semibold">{BUSINESS.phone}</span>
            </a>
            <a
              data-testid="visit-email-link"
              href={BUSINESS.emailHref}
              className="flex items-center gap-3 text-slate-700 hover:text-volt transition-colors duration-300 break-all"
            >
              <Mail className="w-5 h-5 text-volt shrink-0" />
              <span className="font-semibold">{BUSINESS.email}</span>
            </a>
            <p className="flex items-start gap-3 text-slate-700">
              <Clock className="w-5 h-5 text-volt shrink-0 mt-0.5" />
              <span className="font-semibold leading-snug">
                MON–FRI: 9 AM – 6 PM
                <br />
                SAT: 9 AM – 5 PM
              </span>
            </p>
          </address>
          <a
            data-testid="visit-directions-button"
            href={BUSINESS.mapsDirections}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2.5 bg-volt text-white rounded-lg font-display font-bold uppercase tracking-wide text-sm px-7 py-3.5 hover:bg-volt-hover hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            <Navigation className="w-4 h-4" />
            Get Directions
          </a>
          <p className="mt-8 text-sm text-slate-500" data-testid="visit-areas-served">
            <span className="font-bold text-ink">Areas we serve:</span>{" "}
            <Link to="/locations/hayward" className="text-volt font-semibold hover:underline">Hayward</Link>,{" "}
            <Link to="/locations/union-city" className="text-volt font-semibold hover:underline">Union City</Link>,{" "}
            <Link to="/locations/san-leandro" className="text-volt font-semibold hover:underline">San Leandro</Link>
            <span>, Castro Valley, San Lorenzo, Fremont &amp; Newark</span>
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-5">
          <div className="border border-line rounded-xl overflow-hidden shadow-sm h-full min-h-[320px] bg-white" data-testid="visit-map">
            <iframe
              title="BaySmog location map — 258 W Tennyson Rd, Hayward, CA 94544"
              src={BUSINESS.mapsEmbed}
              className="w-full h-full min-h-[320px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </FadeUp>

        <FadeUp delay={0.18} className="lg:col-span-3">
          <h3 className="font-display font-bold text-xl text-ink">Why Choose Us?</h3>
          <ul className="mt-5 space-y-3" data-testid="visit-checklist">
            {VISIT_CHECKLIST.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Check className="w-4 h-4 text-volt shrink-0 mt-0.5" strokeWidth={3} />
                {c}
              </li>
            ))}
          </ul>
          <div className="mt-7 border-2 border-volt rounded-xl bg-white p-5 text-center">
            <Star className="w-6 h-6 text-red-600 fill-red-600 mx-auto" />
            <p className="mt-2 font-display font-black uppercase text-volt leading-tight">
              STAR Certified
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">
              Smog Check Station
            </p>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section data-testid="final-cta" className="relative bg-volt text-white py-20 md:py-24 overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-10">
      <FadeUp>
        <h2 className="font-display font-black tracking-tight leading-[1.02] text-4xl md:text-5xl">
          Need a smog check today?
        </h2>
        <p className="mt-4 font-medium text-white/85 max-w-md">
          Walk in during business hours — most tests done in 15–20 minutes.
        </p>
      </FadeUp>
      <FadeUp delay={0.12} className="flex flex-col sm:flex-row gap-4">
        <a
          data-testid="final-call-button"
          href={BUSINESS.phoneHref}
          className="inline-flex items-center gap-2.5 bg-white text-volt rounded-full font-display font-bold uppercase tracking-wide px-9 py-5 shadow-lg shadow-black/10 hover:-translate-y-1 transition-transform duration-300"
        >
          <Phone className="w-5 h-5" />
          {BUSINESS.phone}
        </a>
        <a
          data-testid="final-directions-button"
          href={BUSINESS.mapsDirections}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2.5 border-2 border-white/80 text-white rounded-full font-display font-bold uppercase tracking-wide px-9 py-5 hover:-translate-y-1 transition-transform duration-300"
        >
          <Navigation className="w-5 h-5" />
          Get Directions
        </a>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <SEO
        title="Fast STAR Smog Check in Hayward, CA | BaySmog — 258 W Tennyson Rd"
        description="BaySmog is a STAR smog check station in Hayward, CA near I-880 & Tennyson Rd. Smog tests, diesel smog, Clean Truck Check & VIN verification. Call (510) 887-4735."
      />
      <Hero />
      <StatsStrip />
      <ServicesCards />
      <WhyChoose />
      <Process />
      <Coupon />
      <ReviewsCTA />
      <FAQ />
      <VisitUs />
      <FinalCTA />
    </>
  );
}
